import routes from 'js/core_pre_webpack/routes';
import { trackClick } from 'js/utils/trackClick';
import { authorLine } from 'js/liswp_blocks/shared/authorLine';
import type { LoginModalEventDetail } from 'js/login_modal/LoginModalManager';
import type {
  DownloadModalDetail,
  DownloadModalPackPreview,
  RegistrationParams,
} from 'js/login_modal/types';

import { packCheckoutOpts } from './checkoutOpts';
import { lazyOpenLoginModal } from './lazyOpenLoginModal';

export const DOWNLOAD_MODAL_OPEN_SOURCE = 'loswp--download-modal--open';

export type DownloadModalMode = 'pdf_only' | 'pdf_pack';

export type DownloadModalPackRow = {
  id: number;
  title: string;
  image?: string;
  authors: Array<{ displayName: string }>;
  publicationYear?: number;
  viewCount?: number;
};

export type OpenDownloadModalArgs = {
  mode: DownloadModalMode;
  workId: number;
  workTitle: string;
  authors: Array<{ displayName: string }>;
  publicationName?: string;
  publicationYear?: number;
  thumbnailUrl?: string;
  attachmentId?: number;
  pack?: {
    priceCopy: string;
    totalCount: number;
    relatedWorks: DownloadModalPackRow[];
    rowViews?: Record<string, number>;
  };
};

function packPreviewFor(
  pack: NonNullable<OpenDownloadModalArgs['pack']>,
): DownloadModalPackPreview {
  return {
    priceCopy: pack.priceCopy,
    relatedCount: pack.totalCount,
    relatedWorks: pack.relatedWorks.map((row) => ({
      id: row.id,
      title: row.title,
      image: row.image,
      authors: row.authors,
      year: row.publicationYear,
    })),
    rowViews:
      pack.rowViews ??
      Object.fromEntries(
        pack.relatedWorks
          .filter((row) => row.viewCount != null)
          .map((row) => [String(row.id), row.viewCount as number]),
      ),
  };
}

export function openDownloadModal({
  mode,
  workId,
  workTitle,
  authors,
  publicationName,
  publicationYear,
  thumbnailUrl,
  attachmentId,
  pack,
}: OpenDownloadModalArgs): Promise<void> {
  trackClick({
    source: DOWNLOAD_MODAL_OPEN_SOURCE,
    entityId: workId,
    extra: { source_work_id: workId, modal_mode: mode },
  });

  const packMode = mode === 'pdf_pack' && pack !== undefined;

  let postLoginRedirectUrl: string;
  let registrationParams: RegistrationParams;
  if (packMode) {
    const checkoutOpts = packCheckoutOpts(workId);
    postLoginRedirectUrl = routes.checkout_url(checkoutOpts);
    registrationParams = {
      user_source: 'download',
      checkout_opts: checkoutOpts,
    };
  } else {
    const redirect = new URL(
      routes.show_work_url({ id: workId }),
      window.location.origin,
    );
    redirect.searchParams.set('auto', 'download');
    postLoginRedirectUrl = redirect.toString();
    registrationParams = {
      user_source: 'download',
      doc_id: attachmentId,
      auto: 'download',
    };
  }

  const downloadModal: DownloadModalDetail = {
    mode,
    title: packMode
      ? `Download PDF Pack for ${pack.priceCopy}`
      : 'Download PDF',
    packPreview: packMode ? packPreviewFor(pack) : undefined,
    workTitle,
    authorLine: authorLine(authors),
    publicationLine: [publicationYear, publicationName]
      .filter(Boolean)
      .join(', '),
    workThumbnailUrl: thumbnailUrl,
  };

  const detail: LoginModalEventDetail = {
    postLoginRedirectUrl,
    registrationParams,
    headerVariant: 'downloadHero',
    ctaLayout: 'googleOnlyExpander',
    configuredText: { buttonsPage: { subheader: '' } },
    clickTrackingContext: {
      entityId: workId,
      extra: { source_work_id: workId, modal_mode: mode },
    },
    downloadModal,
    workTitle,
    authorName: authors[0]?.displayName,
    workThumbnailUrl: thumbnailUrl,
    workYear: publicationYear,
  };

  return lazyOpenLoginModal(detail);
}

export default openDownloadModal;
